import React, { useState } from 'react';
import { Check, Loader2, Shield, Info, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { supabase } from '../supabaseClient';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from './ui/tooltip';
import { useSubscription } from '../contexts/SubscriptionContext';

const plans = [
    {
        name: 'Free',
        price: 0,
        description: 'Perfect for testing',
        features: [
            '1 project',
            { text: '1,000 users/month', help: '1 user = 1 unique session' },
            'Draft/testing mode only',
            'Community support'
        ],
        cta: 'Select',
        popular: false,
        priceId: null
    },
    {
        name: 'Starter',
        price: 19,
        description: 'For growing apps',
        features: [
            'Unlimited projects',
            { text: '25,000 users/month', help: '1 user = 1 unique session' },
            'Publishing enabled',
            'Full analytics dashboard',
            'A/B Testing (up to 3 variants)',
            'Email support',
            { text: 'Need more users?', extra: '$5 per extra 10k users' }
        ],
        cta: 'Get Started',
        popular: true,
        priceId: process.env.REACT_APP_POLAR_STARTER_PRICE_ID
    },
    {
        name: 'Growth',
        price: 49,
        description: 'For scaling products',
        features: [
            'Everything in Starter, plus:',
            { text: '100,000 users/month', help: '1 user = 1 unique session' },
            'Advanced analytics',
            'Unlimited A/B test variants',
            'No branding',
            'Priority email support',
            { text: 'Need more users?', extra: '$5 per extra 10k users' }
        ],
        cta: 'Get Started',
        popular: false,
        priceId: process.env.REACT_APP_POLAR_GROWTH_PRICE_ID
    },
    {
        name: 'Scale',
        price: 199,
        priceLabel: '$199+',
        description: 'For high-volume apps',
        features: [
            'Everything in Growth, plus:',
            { text: '500,000+ users/month', help: '1 user = 1 unique session' },
            'Custom limits & pricing',
            'Dedicated priority support',
            'Custom integrations'
        ],
        cta: 'Get Started',
        popular: false,
        priceId: process.env.REACT_APP_POLAR_SCALE_PRICE_ID
    }
];

export default function PricingPage({ onClose, limitReason }) {
    const { subscription } = useSubscription();
    const currentPlan = subscription?.plan_type || 'free';
    const [loading, setLoading] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);

    const handleManageSubscription = async () => {
        setLoading(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();

            if (subscription?.polar_customer_id) {
                const { data, error } = await supabase.functions.invoke('create-customer-portal', {
                    body: {
                        userId: user.id,
                        customerId: subscription.polar_customer_id
                    }
                });

                if (error) throw error;
                if (data?.url) {
                    window.open(data.url, '_blank');
                    return;
                }
            }

            // Fallback if no customer ID yet or error occurs
            window.open('https://polar.sh/pop-onboarding/portal', '_blank');

        } catch (error) {
            console.error('Error opening portal:', error);
            // Fallback on error
            window.open('https://polar.sh/pop-onboarding/portal', '_blank');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectPlan = async (plan) => {
        if (plan.price === 0) {
            // Free plan - just close
            onClose?.();
            return;
        }



        setLoading(true);
        setSelectedPlan(plan.name);

        try {
            const { data: { user } } = await supabase.auth.getUser();

            if (!user) {
                alert('Please log in to upgrade your plan');
                setLoading(false);
                setSelectedPlan(null);
                return;
            }

            console.log('Initiating checkout for:', plan.name);
            console.log('Price ID:', plan.priceId);

            // Call Supabase Edge Function to create checkout session
            const response = await fetch(
                `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-checkout`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
                    },
                    body: JSON.stringify({
                        priceId: plan.priceId,
                        userId: user.id,
                        email: user.email
                    })
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to create checkout session');
            }

            const { url } = data;

            // Redirect to Polar Checkout
            window.location.href = url;
        } catch (error) {
            console.error('Error creating checkout session:', error);
            alert(`Checkout failed: ${error.message}`);
            setLoading(false);
            setSelectedPlan(null);
        }
    };

    return (
        <div className="py-12 px-4">
            <div className="max-w-7xl mx-auto">
                {limitReason && (
                    <div className="max-w-3xl mx-auto mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-3 text-yellow-800">
                        <Shield className="h-5 w-5" />
                        <p className="font-medium">
                            {typeof limitReason === 'string'
                                ? limitReason
                                : limitReason.reason === 'projects'
                                    ? `You've reached the ${limitReason.limit} project limit on your current plan.`
                                    : limitReason.reason === 'screens'
                                        ? `You've used ${limitReason.current} screens (Limit: ${limitReason.limit}). Upgrade to publish!`
                                        : limitReason.reason === 'analytics'
                                            ? 'Upgrade to access Advanced Analytics!'
                                            : limitReason.reason === 'published_flows'
                                                ? "You've reached your published flows limit. Upgrade to publish more!"
                                                : limitReason.reason === 'publish'
                                                    ? "Publishing projects is only available on paid plans. Upgrade to publish!"
                                                    : 'Upgrade your plan to unlock more features.'}
                        </p>
                    </div>
                )}

                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold mb-4">
                        Free Until You Publish
                    </h1>
                    <p className="text-xl text-muted-foreground">
                        Build, test, and iterate for free. Only pay when you're ready to go live.
                    </p>
                    {currentPlan !== 'free' && (
                        <div className="flex flex-col items-center gap-2 mt-2">
                            <p className="text-sm text-muted-foreground">
                                Current plan: <span className="font-semibold capitalize">{currentPlan}</span>
                            </p>
                            <Button
                                variant="link"
                                size="sm"
                                className="text-muted-foreground hover:text-primary h-auto p-0"
                                onClick={handleManageSubscription}
                                disabled={loading}
                            >
                                Manage Billing <ExternalLink className="ml-1 h-3 w-3" />
                            </Button>
                        </div>
                    )}
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
                    {plans.map((plan) => {
                        const isCurrentPlan = plan.name.toLowerCase() === currentPlan;
                        const isLoading = loading && selectedPlan === plan.name;

                        return (
                            <Card
                                key={plan.name}
                                className={`p-8 relative ${plan.popular ? 'border-primary border-2 shadow-lg scale-105' : ''}`}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                        <div className="bg-primary text-primary-foreground text-sm font-semibold px-4 py-1 rounded-full">
                                            Most Popular
                                        </div>
                                    </div>
                                )}

                                <div className="text-center mb-6">
                                    <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                                    <p className="text-muted-foreground text-sm mb-4">{plan.description}</p>

                                    <div className="mb-6">
                                        <span className="text-5xl font-bold">{plan.priceLabel || `$${plan.price}`}</span>
                                        <span className="text-muted-foreground">/month</span>
                                    </div>
                                </div>

                                <Button
                                    className="w-full mb-6"
                                    variant={plan.popular ? 'default' : 'outline'}
                                    onClick={() => handleSelectPlan(plan)}
                                    disabled={loading || isCurrentPlan}
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Loading...
                                        </>
                                    ) : isCurrentPlan ? (
                                        'Current Plan'
                                    ) : (
                                        plan.cta
                                    )}
                                </Button>

                                <TooltipProvider delayDuration={0}>
                                    <ul className="space-y-3">
                                        {plan.features.map((feature, idx) => {
                                            // Handle object features (with help text, badges, or extra info)
                                            if (typeof feature === 'object' && feature.text) {
                                                return (
                                                    <li key={idx} className="flex items-start gap-3">
                                                        <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                                        <div className="flex flex-col">
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="text-sm">{feature.text}</span>
                                                                {feature.badge && (
                                                                    <span className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded font-semibold">
                                                                        {feature.badge}
                                                                    </span>
                                                                )}
                                                                {feature.help && (
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <div className="cursor-help">
                                                                                <Info className="h-4 w-4 text-muted-foreground/70 hover:text-foreground transition-colors" />
                                                                            </div>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent className="z-[60]">
                                                                            <p>{feature.help}</p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                )}
                                                            </div>
                                                            {feature.extra && (
                                                                <span className="text-xs text-muted-foreground mt-0.5">{feature.extra}</span>
                                                            )}
                                                        </div>
                                                    </li>
                                                );
                                            }

                                            // Handle string features
                                            return (
                                                <li key={idx} className="flex items-start gap-3">
                                                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                                    <span className="text-sm">{feature}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </TooltipProvider>
                            </Card>
                        );
                    })}
                </div>

                {/* Need Extra Views Section - Only for paid subscribers */}
                <div className="mt-12 p-6 bg-muted/50 rounded-lg text-center max-w-2xl mx-auto border border-dashed border-primary/20">
                    <h3 className="text-lg font-semibold mb-4">Need Extra Users?</h3>
                    {subscription?.plan_type && subscription.plan_type !== 'free' ? (
                        <>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                                <Button
                                    variant="outline"
                                    onClick={() => handleSelectPlan({
                                        name: '10k Views Pack',
                                        priceId: process.env.REACT_APP_POLAR_10K_VIEWS_PRICE_ID,
                                        oneTime: true
                                    })}
                                >
                                    <span className="font-semibold mr-2">+10,000 Users</span>
                                    <span className="bg-primary/10 text-primary px-2 py-0.5 rounded text-xs">$5 one-time</span>
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground mt-4">
                                Users are added to your monthly limit instantly.
                                <strong> Note:</strong> Extra users expire at the end of your billing cycle.
                            </p>
                        </>
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            Upgrade to a paid plan to purchase additional users.
                        </p>
                    )}
                </div>

                {/* FAQ Section */}
                <div className="mt-16 text-left max-w-3xl mx-auto">
                    <h2 className="text-2xl font-bold mb-6 text-center">Frequently Asked Questions</h2>
                    <div className="space-y-6">
                        <div>
                            <h3 className="font-semibold text-lg mb-2">What happens if I exceed my user limit?</h3>
                            <p className="text-muted-foreground">
                                We believe in "Grace Periods". If you exceed your limit, your onboarding flows
                                <strong> will continue to show to your users</strong> for 10 days (Gratitude Period).
                                However, you will be blocked from making new edits in the editor until you upgrade
                                or add more users.
                            </p>
                        </div>
                        <div>
                            <h3 className="font-semibold text-lg mb-2">Do you charge for overages automatically?</h3>
                            <p className="text-muted-foreground">
                                On the Starter and Growth plans, overages are billed at the end of the billing cycle
                                if you choose to enable auto-scaling. Otherwise, we enforce the grace period logic above.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="text-center mt-12 text-sm text-muted-foreground">
                    <p>Cancel anytime. No long-term contracts.</p>
                    <p className="mt-2">Need a custom plan? <a href="mailto:jagib07@gmail.com" className="text-primary hover:underline">Contact us</a></p>
                </div>
            </div>
        </div>
    );
}
